import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';

import { informacionCorte, limpiarCorte } from '../../actions/cortes';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const formatFechaCorta = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

const InformacionCorte = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const informacion = useSelector(state => state.cortes.informacion);

    const corte   = informacion?.corte;
    const ind     = informacion?.indicadores;
    const dm      = informacion?.desglose_metodos || [];
    const totales = corte?.totales || [];

    const [asistenciaDetalle, setAsistenciaDetalle] = useState(null);

    useEffect(() => {
        if (id) {
            dispatch(informacionCorte(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Cortes', path: '/cortes', activo: false },
                { nombre: 'Información del corte', path: `/cortes/informacion/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarCorte());
        };
    }, [id]);

    const kpisRow1 = [
        { label: 'Ventas',        sublabel: 'Número', value: ind?.ventas?.numero  ?? '-', icon: 'fa-shopping-cart', color: 'success' },
        { label: 'Ventas',        sublabel: 'Total',  value: ind?.ventas?.total   != null ? formatNumber(ind.ventas.total, 2, '$', '')  : '-', icon: 'fa-dollar-sign', color: 'success' },
        { label: 'Ventas',        sublabel: 'Conos (totales / comisionados)', value: (ind?.ventas?.conos ?? '-') + ' / ' + (ind?.ventas?.conos_comisionados ?? '-'), icon: 'fa-ice-cream', color: 'success' },
        { label: 'V. ambulantes', sublabel: 'Número', value: ind?.vueltas?.numero ?? '-', icon: 'fa-redo',          color: 'warning' },
        { label: 'V. ambulantes', sublabel: 'Total',  value: ind?.vueltas?.total  != null ? formatNumber(ind.vueltas.total, 2, '$', '') : '-', icon: 'fa-dollar-sign', color: 'warning' },
        { label: 'V. ambulantes', sublabel: 'Conos (vendidos / comisionados)',  value: (ind?.vueltas?.conos ?? '-') + ' / ' + (ind?.vueltas?.conos ?? '-')  ?? '-', icon: 'fa-ice-cream',     color: 'warning' },
    ];

    const conosVendidos = (ind?.ventas?.conos ?? 0) + (ind?.vueltas?.conos ?? 0);
    const conosComisionados = (ind?.ventas?.conos_comisionados ?? 0) + (ind?.vueltas?.conos ?? 0);

    const kpisRow2 = [
        { label: 'Gastos', sublabel: 'Número',        value: ind?.gastos?.numero        ?? '-', icon: 'fa-file-invoice-dollar', color: 'danger'  },
        { label: 'Gastos', sublabel: 'Total',         value: ind?.gastos?.total != null ? formatNumber(ind.gastos.total, 2, '$', '') : '-', icon: 'fa-dollar-sign', color: 'danger' },
        { label: 'Conos',  sublabel: 'Vendidos / Comisionados',      value: (conosVendidos ?? '-') + ' / ' + (conosComisionados ?? '-'),                     icon: 'fa-ice-cream',           color: 'info' },
        { label: 'Conos',  sublabel: 'Iniciales',     value: ind?.conos?.iniciales      ?? '-', icon: 'fa-ice-cream',           color: 'info'    },
        { label: 'Conos',  sublabel: 'Reabastecidos', value: ind?.conos?.reabastecidos  ?? '-', icon: 'fa-plus-circle',         color: 'info'    },
        { label: 'Conos',  sublabel: 'Finales (reales / calculados)', value: (ind?.conos?.finales ?? '-') + ' / ' + (((ind?.conos?.iniciales || 0) + (ind?.conos?.reabastecidos || 0) - conosVendidos) ?? '-')        ?? '-', icon: 'fa-flag-checkered',      color: 'info'    },
    ];

    const columnsVentas = [
        { name: 'Folio',    sortable: true, wrap: true,            selector: row => row.folio || '-' },
        { name: 'Fecha',    sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Método',   sortable: true, wrap: true,            selector: row => row.metodo?.nombre || '-' },
        { name: 'Vendedor', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.vendedor) },
        { name: 'Total',    sortable: true, wrap: true,            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        }
    ];

    const columnsVueltas = [
        { name: 'Folio',       sortable: true, wrap: true,            selector: row => row.folio || '-' },
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Vendedor',    sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.vendedor) },
        { name: 'Responsable', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Total',       sortable: true, wrap: true,            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizada' : 'Pendiente'}
                </span>
            )
        }
    ];

    const columnsGastos = [
        { name: 'Concepto',  sortable: true, wrap: true, grow: 2,   selector: row => row.concepto || '-' },
        { name: 'Fecha',     sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha) },
        { name: 'Proveedor', sortable: true, wrap: true, grow: 1.5, selector: row => row.proveedor?.nombre || '-' },
        { name: 'Método',    sortable: true, wrap: true,            selector: row => row.metodo?.nombre || '-' },
        { name: 'Total',     sortable: true, wrap: true,            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' }
    ];

    const columnsAsistencias = [
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha) },
        { name: 'Responsable', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Empleados',   sortable: true, wrap: true,            selector: row => row.total_empleados ?? '-' },
        { name: 'Asistencias', sortable: true, wrap: true,            selector: row => row.total_asistencias ?? '-' },
        {
            name: '', width: '60px',
            cell: row => (
                <button className="btn btn-sm btn-info" onClick={() => setAsistenciaDetalle(row)}>
                    <i className="fas fa-eye"></i>
                </button>
            )
        }
    ];

    const columnsRevisiones = [
        { name: 'Fecha creación', sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha_creacion) },
        { name: 'Fecha revisión', sortable: true, wrap: true, grow: 1.5, selector: row => row.fecha_revision ? formatFecha(row.fecha_revision) : '-' },
        { name: 'Responsable',    sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Confirmador',    sortable: true, wrap: true, grow: 2,   selector: row => row.confirmador ? formatNombre(row.confirmador) : 'Pendiente' },
        { name: 'Puntos',         sortable: true, wrap: true,            selector: row => row.total_puntos != null ? `${row.puntos_bien ?? 0} / ${row.total_puntos}` : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Revisada' : 'Pendiente'}
                </span>
            )
        }
    ];

    const columnsPagosVueltas = [
        { name: 'Responsable', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFecha(row.fecha) },
        { name: 'Método',      sortable: true, wrap: true,            selector: row => row.metodo?.nombre || '-' },
        { name: 'V. amb.',     sortable: true, wrap: true,            selector: row => row.vueltas?.length ?? '-' },
        { name: 'Total',       sortable: true, wrap: true,            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' }
    ];

    const columnsConos = [
        { name: 'Fecha',       sortable: true, wrap: true, grow: 1.5, selector: row => formatFechaCorta(row.fecha) },
        { name: 'Responsable', sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Cantidad',    sortable: true, wrap: true,            selector: row => row.cantidad ?? '-' }
    ];

    const paginationOpts = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const noDataComponent = (texto) => (
        <div className="py-4 text-center text-muted">{texto}</div>
    );

    const secciones = [
        { id: 'icVentas',        titulo: 'Ventas',                     icon: 'fa-shopping-cart',       color: 'success', columns: columnsVentas,       data: informacion?.ventas        || [], empty: 'No hay ventas en este corte' },
        { id: 'icVueltas',       titulo: 'Ventas ambulantes',           icon: 'fa-redo',                color: 'warning', columns: columnsVueltas,       data: informacion?.vueltas       || [], empty: 'No hay ventas ambulantes en este corte' },
        { id: 'icPagosVueltas',  titulo: 'Pagos de ventas ambulantes',  icon: 'fa-money-bill-wave',     color: 'success', columns: columnsPagosVueltas,  data: informacion?.pagos_vueltas || [], empty: 'No hay pagos de ventas ambulantes en este corte' },
        { id: 'icGastos',        titulo: 'Gastos',                      icon: 'fa-file-invoice-dollar', color: 'danger',  columns: columnsGastos,        data: informacion?.gastos        || [], empty: 'No hay gastos en este corte' },
        { id: 'icAsistencias',   titulo: 'Asistencias',                 icon: 'fa-clipboard-check',     color: 'info',    columns: columnsAsistencias,   data: informacion?.asistencias   || [], empty: 'No hay asistencias en este corte' },
        { id: 'icRevisiones',    titulo: 'Revisiones',                  icon: 'fa-search',              color: 'primary', columns: columnsRevisiones,    data: informacion?.revisiones    || [], empty: 'No hay revisiones en este corte' },
        { id: 'icConos',         titulo: 'Reabastecimientos de conos',  icon: 'fa-ice-cream',           color: 'success', columns: columnsConos,         data: informacion?.conos         || [], empty: 'No hay reabastecimientos en este corte' },
    ];

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-cash-register text-danger fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">Corte {corte?.folio || ''}</h4>
                        <small className="text-muted">Información completa del corte</small>
                    </div>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        {corte && (
                            <span className={`badge fs-6 px-3 py-2 ${corte.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                                <i className={`fas ${corte.status === true ? 'fa-check-circle' : 'fa-clock'} me-1`}></i>
                                {corte.status === true ? 'Finalizado' : 'Pendiente'}
                            </span>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/cortes')}>
                            <i className="fas fa-arrow-left me-1"></i> Volver
                        </button>
                    </div>
                </div>
            </div>

            {/* KPIs fila 1 */}
            <div className="col-12 mb-2">
                <div className="row g-3">
                    {kpisRow1.map(({ label, sublabel, value, icon, color }) => (
                        <div className="col-md-2" key={`${label}-${sublabel}`}>
                            <div className="text-center p-3 rounded bg-light">
                                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-2`} style={{ width: 40, height: 40 }}>
                                    <i className={`fas ${icon} text-${color}`}></i>
                                </div>
                                <div className="text-muted small">{label}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sublabel}</div>
                                <div className="fw-bold">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* KPIs fila 2 */}
            <div className="col-12 mb-4">
                <div className="row g-3">
                    {kpisRow2.map(({ label, sublabel, value, icon, color }) => (
                        <div className="col-md" key={`${label}-${sublabel}`}>
                            <div className="text-center p-3 rounded bg-light">
                                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-2`} style={{ width: 40, height: 40 }}>
                                    <i className={`fas ${icon} text-${color}`}></i>
                                </div>
                                <div className="text-muted small">{label}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sublabel}</div>
                                <div className="fw-bold">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Acordeón */}
            <div className="col-12">
                <div className="accordion" id="accordionInformacionCorte">

                    {/* Sección 1: Información del corte */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="icHeadingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#icCollapseInfo" aria-expanded="true" aria-controls="icCollapseInfo">
                                <i className="fas fa-info-circle text-primary me-2"></i>
                                Información del corte
                            </button>
                        </h2>
                        <div id="icCollapseInfo" className="accordion-collapse collapse show" aria-labelledby="icHeadingInfo" data-bs-parent="#accordionInformacionCorte">
                            <div className="accordion-body">
                                <div className="row g-3">
                                    <div className="col-md-1">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Folio</div>
                                            <div className="fw-semibold">{corte?.folio || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Fecha</div>
                                            <div className="fw-semibold">{formatFecha(corte?.fecha)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-info border-3 ps-3">
                                            <div className="text-muted small mb-1">Sucursal</div>
                                            <div className="fw-semibold">{corte?.sucursal?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-info border-3 ps-3">
                                            <div className="text-muted small mb-1">Evento</div>
                                            <div className="fw-semibold">{corte?.evento?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="border-start border-info border-3 ps-3">
                                            <div className="text-muted small mb-1">Responsable</div>
                                            <div className="fw-semibold">{formatNombre(corte?.responsable)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-secondary border-3 ps-3">
                                            <div className="text-muted small mb-1">Efectivo inicial</div>
                                            <div className="fw-semibold">{corte?.total_inicial != null ? formatNumber(corte.total_inicial, 2, '$', '') : '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border-start border-warning border-3 ps-3">
                                            <div className="text-muted small mb-1">Observaciones de conos</div>
                                            <div className="fw-semibold">{corte?.observaciones_conos || 'Sin observaciones'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-5">
                                        <div className="border-start border-warning border-3 ps-3">
                                            <div className="text-muted small mb-1">Observaciones de métodos de pago</div>
                                            <div className="fw-semibold">{corte?.observaciones_metodos || 'Sin observaciones'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección 2: Desglose por método de pago */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="icHeadingDesglose">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icCollapseDesglose" aria-expanded="false" aria-controls="icCollapseDesglose">
                                <i className="fas fa-credit-card text-secondary me-2"></i>
                                Desglose por método de pago
                                {dm.length > 0 && (
                                    <span className="badge bg-secondary rounded-pill ms-2">{dm.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="icCollapseDesglose" className="accordion-collapse collapse" aria-labelledby="icHeadingDesglose" data-bs-parent="#accordionInformacionCorte">
                            <div className="accordion-body">
                                <div className="row g-3">
                                    {dm.map((m, i) => {
                                        const real = totales.find(t => {
                                            const tId = t.metodo?._id ?? t.metodo;
                                            return tId && m.metodo?._id && String(tId) === String(m.metodo._id);
                                        });
                                        const calculado = m.total ?? null;
                                        const realTotal = real?.total ?? null;
                                        const diff = calculado != null && realTotal != null ? realTotal - calculado : null;
                                        const diffColor = diff === null ? 'secondary' : Math.abs(diff) < 0.01 ? 'success' : 'danger';

                                        return (
                                            <div className="col-md-auto" key={i}>
                                                <div className={`border-start border-${diffColor} border-3 ps-3`}>
                                                    <div className="text-muted small mb-1 fw-semibold">{m.metodo?.nombre || '-'}</div>
                                                    <div className="d-flex gap-3">
                                                        <div>
                                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>Calculado</div>
                                                            <div className="fw-bold">{calculado != null ? formatNumber(calculado, 2, '$', '') : '-'}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>Real</div>
                                                            <div className="fw-bold">{realTotal != null ? formatNumber(realTotal, 2, '$', '') : '-'}</div>
                                                        </div>
                                                        {diff !== null && (
                                                            <div>
                                                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Diferencia</div>
                                                                <div className={`fw-bold text-${diffColor}`}>
                                                                    {diff >= 0 ? '+' : ''}{formatNumber(diff, 2, '$', '')}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Secciones de listados */}
                    {secciones.map(({ id, titulo, icon, color, columns, data, empty }) => (
                        <div className="accordion-item border-0 shadow-sm mb-2" key={id}>
                            <h2 className="accordion-header" id={`${id}Heading`}>
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#${id}Collapse`} aria-expanded="false" aria-controls={`${id}Collapse`}>
                                    <i className={`fas ${icon} text-${color} me-2`}></i>
                                    {titulo}
                                    {data.length > 0 && (
                                        <span className={`badge bg-${color} rounded-pill ms-2`}>{data.length}</span>
                                    )}
                                </button>
                            </h2>
                            <div id={`${id}Collapse`} className="accordion-collapse collapse" aria-labelledby={`${id}Heading`} data-bs-parent="#accordionInformacionCorte">
                                <div className="accordion-body p-0">
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationPerPage={20}
                                        paginationComponentOptions={paginationOpts}
                                        highlightOnHover
                                        noDataComponent={noDataComponent(empty)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            {/* Modal detalle de asistencia */}
            <Modal show={!!asistenciaDetalle} size="lg" backdrop="static" centered onHide={() => setAsistenciaDetalle(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-clipboard-check text-info me-2"></i>
                        Asistencia — {formatFechaCorta(asistenciaDetalle?.fecha)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <DataTable
                        columns={[
                            { name: 'Empleado',   sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.empleado) },
                            { name: 'Sucursal',   sortable: true, wrap: true, grow: 2,   selector: row => row.sucursal?.nombre || '-' },
                            { name: 'Evento',     sortable: true, wrap: true, grow: 2,   selector: row => row.evento?.nombre   || '-' },
                            {
                                name: 'Asistencia', sortable: true,
                                cell: row => (
                                    <span className={`badge ${row.asistencia ? 'bg-success' : 'bg-danger'}`}>
                                        {row.asistencia ? 'Asistió' : 'No asistió'}
                                    </span>
                                )
                            }
                        ]}
                        data={asistenciaDetalle?.empleados || []}
                        pagination
                        paginationPerPage={20}
                        paginationComponentOptions={paginationOpts}
                        highlightOnHover
                        noDataComponent={<div className="py-4 text-center text-muted">Sin empleados registrados</div>}
                    />
                </Modal.Body>
            </Modal>

        </div>
    );
};

export default InformacionCorte;
